import { useState, useRef, useMemo } from 'react'
import { Form, Input, InputNumber, Popconfirm, Space, Select, Table } from 'antd'
import { errorMessage } from '../../../../helpers/errorMessageFunc'
import HideOnMobile from '../../../../components/generic/hideOnMobile'
import InitializePaymentsFromCSV from '../../../../components/specific/payments/initializePaymentsFromCSV'
import GenericDropdown from '../../../../components/generic/genericDropdown'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ReviewPaymentRequests from './reviewPaymentRequests'
import HeaderBox from '../../../../components/generic/arpariBoxes/headerBox'
import Stack from '@mui/material/Stack'

const { TextArea } = Input
const { Option } = Select

const ExternalPayments = (props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [dataSource, setDataSource] = useState([])
  const [displayData, setDisplayData] = useState([])
  const [formData, setFormData] = useState({})

  const formRef = useRef(null)

  const sortedRecipients = useMemo(() => {
    return [...props.recipients].sort((a, b) => {
      if (!a.name) return 1
      if (!b.name) return -1
      return a.name?.localeCompare(b.name, undefined, { sensitivity: 'base' }) || 0
    })
  }, [props.recipients])

  const accountHolderRender = (name, nickname) => {
    if (name === nickname || nickname === undefined || nickname === '') {
      return name
    }
    return `${name} (${nickname})`
  }
  const resetPaymentForm = () => {
    setCurrentIndex(0)
    setShowReviewModal(false)
    setDataSource([])

    if (formRef.current) {
      formRef.current.resetFields()
    }
  }

  const handleChange = (input) => (e, selectedOption) => {
    let value = undefined
    if (Array.isArray(e)) {
      value = e.slice(-1)[0]
    } else if ((typeof e === 'string') | (typeof e === 'number')) {
      value = e
    } else if (e.target !== undefined) {
      value = e.target.value
    } else {
      value = e.format('YYYY-MM-DD')
    }
    setFormData((prevState) => {
      const newState = { ...prevState }
      newState[input] = value
      return newState
    })
  }

  const createNewRow = (
    newCurrentIndex,
    initialSendingAccount,
    initialRecipient,
    initialDescription,
    initialAmount
  ) => {
    const newRow = {
      key: newCurrentIndex,
      from: (
        <Form.Item
          label='Sending Account'
          name={'sendingAccount' + newCurrentIndex}
          initialValue={initialSendingAccount || ''}
        >
          <Select
            placeholder='Select a bank account'
            allowClear
            showSearch
            dropdownMatchSelectWidth={false}
            optionFilterProp='children'
            defaultValue={initialSendingAccount || ''}
            onChange={handleChange('sendingAccount' + newCurrentIndex)}
          >
            {props.internalAccounts.map((account) => (
              <Option key={account.id} value={account.id}>
                {accountDescriptor(account)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
      to: (
        <Form.Item
          label='Recipient'
          name={'receivingCounterparty' + newCurrentIndex}
          initialValue={initialRecipient || ''}
        >
          <Select
            placeholder='Select a bank account'
            allowClear
            showSearch
            dropdownMatchSelectWidth={false}
            optionFilterProp='children'
            defaultValue={initialRecipient || ''}
            onChange={handleChange('receivingCounterparty' + newCurrentIndex)}
          >
            {sortedRecipients.length !== 0 ? (
              sortedRecipients.map((recipient) => (
                <Option key={recipient.id} value={recipient.id}>
                  {accountHolderRender(recipient.name, recipient.nickname)}
                </Option>
              ))
            ) : (
              <Option value=''>Please save a recipient first.</Option>
            )}
          </Select>
        </Form.Item>
      ),
      description: (
        <Form.Item
          label='Description'
          name={'description' + newCurrentIndex}
          onChange={handleChange('description' + newCurrentIndex)}
          initialValue={initialDescription || ''}
        >
          <TextArea showCount maxLength={50} initialvalue={initialDescription || ''} />
        </Form.Item>
      ),
      amount: (
        <Form.Item
          label='Amount'
          initialValue={initialAmount || ''}
          name={'amount' + newCurrentIndex}
          rules={[
            {
              required: true,
              message: 'Please input the amount.',
            },
          ]}
        >
          <InputNumber
            placeholder='$0.00'
            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            onChange={handleChange('amount' + newCurrentIndex)}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            style={{ width: '100%' }}
            initialValue={initialAmount || ''}
          />
        </Form.Item>
      ),
    }

    return [newRow, newCurrentIndex]
  }

  const addNewRow = (
    initialSendingAccount = undefined,
    initialRecipient = undefined,
    initialDescription = undefined,
    initialAmount = undefined
  ) => {
    return new Promise((resolve) => {
      setCurrentIndex((prevState) => {
        resolve(prevState + 1)
        return prevState + 1
      })
    }).then((newCurrentIndex) => {
      let res = createNewRow(
        newCurrentIndex,
        initialSendingAccount,
        initialRecipient,
        initialDescription,
        initialAmount
      )
      setDataSource((prevState) => {
        return [...prevState, res[0]]
      })
    })
  }

  const accountDescriptor = (acct) => {
    return `${acct.beneficiary.name} / ${acct.account.account_description} / ${accountBalance(acct)}`
  }

  const accountBalance = (acct) => {
    return acct.account.available_balance
  }

  const onFormSubmit = (values) => {
    let data = { ...formData }
    let displayData = []
    let totalamount = 0
    for (let i = 1; i <= currentIndex + 1; i++) {
      if (values['amount' + i] !== undefined) {
        if (values['amount' + i] <= 0) {
          errorMessage('Please ensure all amounts are valid.')
          return
        }
        const sendingAccountId = data['sendingAccount' + i]
        const receivingCounterpartyId = data['receivingCounterparty' + i]

        const sendingAccount = props.internalAccounts.find((account) => account.id === sendingAccountId)
        const receivingCounterparty = props.recipients.find((recipient) => recipient.id === receivingCounterpartyId)

        if (!sendingAccount || !receivingCounterparty) {
          errorMessage('Account not found. Please check your selection.')
          return
        }

        const item = {
          sendingAccount: {
            id: sendingAccountId,
            description: accountDescriptor(sendingAccount),
            balance: accountBalance(sendingAccount),
          },
          receivingCounterparty: {
            id: receivingCounterpartyId,
            description: receivingCounterparty.name,
          },
          method: 'Payment',
          amount: data['amount' + i],
          description: data['description' + i],
        }
        displayData.push(item)
        totalamount += parseFloat(data['amount' + i])
      }
    }

    if (displayData.length === 0) {
      errorMessage('Please add at least one row.')
      return
    }
    const item = {
      sendingAccount: '',
      receivingCounterparty: '',
      description: '',
      method: 'Total',
      amount: totalamount,
    }
    displayData.push(item)

    setDisplayData(displayData)
    setShowReviewModal(true)
  }

  const deleteRow = (index) => {
    setDataSource((prevState) => {
      const filteredRows = prevState.filter((row) => row.key !== index)
      return filteredRows
    })
  }

  const closeReviewModal = () => {
    setShowReviewModal(false)
  }

  const handleCSVUpload = (csvData) => {
    const updateAccount = (accountKey, last4, amount, description, currIndex) => {
      if (last4 in props.last4ToInternalAccountMap) {
        const { id } = props.last4ToInternalAccountMap[last4]
        setFormData((prevState) => ({
          ...prevState,
          [`amount${currIndex}`]: parseFloat(amount.replace(/\$|,/g, '')),
          [`description${currIndex}`]: description,
          [`${accountKey}${currIndex}`]: id,
        }))
      }
    }
    let currIndex = currentIndex + 1
    for (const csvRow of csvData) {
      const { fromLast4, toLast4, description, amount } = csvRow
      let fromAcct = props.last4ToInternalAccountMap[fromLast4]
      let toAcct = props.last4ToInternalAccountMap[toLast4]
      if (fromAcct && toAcct) {
        let fromDescription = accountDescriptor(fromAcct)
        let toDescription = accountDescriptor(toAcct)
        addNewRow(fromDescription, toDescription, description, amount)
        updateAccount('sendingAccount', fromLast4, amount, description, currIndex)
        updateAccount('receivingAccount', toLast4, amount, description, currIndex)
        currIndex++
      }
    }
  }

  const defaultColumns = [
    {
      title: 'To/From',
      width: '40%',
      render: (record) => {
        return (
          <div>
            {record['from']}
            {record['to']}
          </div>
        )
      },
    },
    {
      title: 'Instructions',
      width: '55%',
      render: (record) => {
        return (
          <div>
            {record['description']}
            {record['amount']}
          </div>
        )
      },
    },
    {
      // title: "Generate Code",
      width: '5%',
      dataIndex: 'generate',
      render: (_, record) => {
        const currentIndex = record.key
        return (
          <Form.Item>
            <GenericDropdown
              dropdownItems={[
                <Popconfirm
                  title='Sure to cancel?'
                  onConfirm={() => {
                    deleteRow(currentIndex)
                  }}
                >
                  <div
                    style={{
                      border: '0px',
                      color: 'red',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </div>
                </Popconfirm>,
              ]}
            />
          </Form.Item>
        )
      },
    },
  ]

  return (
    <Stack spacing={1}>
      <HeaderBox
        title='External Payments'
        ignoreDocumentTitle={true}
        actions={
          <HideOnMobile>
            <Space>
              <Button
                sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                disableElevation
                fullWidth
                size='small'
                type='submit'
                variant='outlined'
                color='secondary'
                onClick={() => addNewRow()}
              >
                Add Row
              </Button>
              <InitializePaymentsFromCSV paymentType={'external'} onSuccess={handleCSVUpload} />
            </Space>
          </HideOnMobile>
        }
      />
      <>
        {props.loadingInternalAccounts ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100px',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            <Form ref={formRef} onFinish={onFormSubmit} labelAlign='right' labelCol={{ span: 4 }} labelWrap={true}>
              <Table bordered dataSource={dataSource} columns={defaultColumns} pagination={false} tableLayout='fixed' />

              <Form.Item>
                <HideOnMobile>
                  <Button
                    disableElevation
                    fullWidth
                    size='small'
                    type='submit'
                    variant='contained'
                    color='primary'
                    sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                    htmlType='submit'
                    style={{ marginTop: '18px', width: '100px' }}
                  >
                    Submit
                  </Button>
                </HideOnMobile>
              </Form.Item>
            </Form>
            {showReviewModal && (
              <ReviewPaymentRequests
                displayData={displayData}
                show={showReviewModal}
                closeReviewModal={closeReviewModal}
                paymentType='external'
                resetPaymentForm={resetPaymentForm}
                deleteRow={deleteRow}
                setCreatedPaymentRequests={props.setCreatedPaymentRequests}
                setFailedPaymentRequests={props.setFailedPaymentRequests}
              />
            )}
          </>
        )}
      </>
    </Stack>
  )
}

export default ExternalPayments
